/**
 * The inline no-access state a viewer sees when they may not open pool
 * settings. This is the visible half of the Q1 part B decision from
 * ISSUE-512's open-questions record (dated 2026-08-22): an
 * expected refusal reads as a plain explanation with a way out, not as a
 * system error modal. The shared `showApiError` modal is deliberately not
 * raised for this case. ISSUE-483's pool-home pane renders the identical
 * `membership` copy so the two screens agree.
 */
import { Button, Card } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  POOL_NO_ACCESS_ACTION_LABEL,
  POOL_NO_ACCESS_BODY,
  POOL_NO_ACCESS_TITLE,
} from "../../errors/poolAccess";
import {
  POOL_SETTINGS_ROLE_ACTION_LABEL,
  POOL_SETTINGS_ROLE_BODY,
  POOL_SETTINGS_ROLE_TITLE,
} from "./poolSettingsState";

export type typePoolSettingsNoAccessProps = {
  reason: "membership" | "role";
  /** Omitted inside the pool's Settings tab: the pool header's back arrow is the one way out there (ISSUE-1035). */
  onGoBack?: (() => void) | undefined;
};

const COPY = {
  membership: {
    title: POOL_NO_ACCESS_TITLE,
    body: POOL_NO_ACCESS_BODY,
    actionLabel: POOL_NO_ACCESS_ACTION_LABEL,
  },
  role: {
    title: POOL_SETTINGS_ROLE_TITLE,
    body: POOL_SETTINGS_ROLE_BODY,
    actionLabel: POOL_SETTINGS_ROLE_ACTION_LABEL,
  },
} as const;

export const PoolSettingsNoAccess = ({
  reason,
  onGoBack,
}: typePoolSettingsNoAccessProps): ReactNode => {
  const copy = COPY[reason];
  return (
    <Card data-testid="pool-settings-no-access" data-reason={reason}>
      <p className="text-body text-ink">{copy.title}</p>
      <p className="text-body text-ink-secondary">{copy.body}</p>
      {onGoBack === undefined ? null : (
        <Button variant="secondary" size="md" onClick={onGoBack}>
          {copy.actionLabel}
        </Button>
      )}
    </Card>
  );
};
